import ValidationErrors from '@/components/validation-errors';
import {
  api_key_path,
  api_projects_path,
  api_report_path,
  api_reports_path,
  destroy_session_path,
  profile_path,
} from '@/rails/routes';
import type { ProfilesShow, WorkspacePageProps } from '@/types';
import { Avatar } from '@astryxdesign/core/Avatar';
import { Badge } from '@astryxdesign/core/Badge';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Code } from '@astryxdesign/core/Code';
import { Collapsible } from '@astryxdesign/core/Collapsible';
import { FileInput } from '@astryxdesign/core/FileInput';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { List, ListItem } from '@astryxdesign/core/List';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';
import { VStack } from '@astryxdesign/core/VStack';
import { Form, router, useForm, usePage } from '@inertiajs/react';
import { useDirtyGuard } from '@/components/dirty-guard';
import { LogOut } from 'lucide-react';
import { useState, type FormEvent } from 'react';

type ProfileForm = {
  username: string;
  profilePhoto: File | null;
};

type Props = WorkspacePageProps & ProfilesShow;

const Show = () => {
  const { apiKey, errors, profile, auth } = usePage<Props>().props;
  const [isEditing, setIsEditing] = useState(errors.length > 0);
  const form = useForm<ProfileForm>({
    username: profile.username ?? '',
    profilePhoto: null,
  });
  // Avisar antes de salir del perfil con cambios sin guardar.
  useDirtyGuard(form.isDirty);

  const usernameError = errors.find((error) => /username/i.test(error));
  const usernameStatus = usernameError
    ? { type: 'error' as const, message: usernameError }
    : undefined;
  const bannerErrors = usernameError ? errors.filter((error) => error !== usernameError) : errors;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    form.transform((data) => {
      const user: { username: string; profilePhoto?: File } = { username: data.username };

      if (data.profilePhoto) {
        user.profilePhoto = data.profilePhoto;
      }

      return { user };
    });
    form.patch(profile_path(), {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        form.setDefaults({ ...form.data, profilePhoto: null });
        form.reset('profilePhoto');
        setIsEditing(false);
      },
    });
  };

  const cancelEditing = () => {
    form.reset();
    setIsEditing(false);
  };

  return (
    <VStack gap={6} width="100%" className="iorio-user-show-page">
      <section className="iorio-users-header" aria-labelledby="profile-title">
        <VStack gap={1}>
          <Heading level={1} id="profile-title">
            Perfil
          </Heading>
          <Text type="body" color="secondary">
            Así te ve tu equipo en los partes diarios.
          </Text>
        </VStack>
      </section>

      <section className="iorio-profile-sections" aria-label="Perfil de usuario">
        <Card padding={0} className="iorio-users-card iorio-profile-card">
          <VStack gap={0}>
            <HStack gap={4} vAlign="center" className="iorio-profile-identity">
              <Avatar
                name={profile.displayName}
                src={profile.profilePhotoUrl ?? undefined}
                size={96}
              />
              <VStack gap={1} className="iorio-profile-identity-copy">
                <HStack gap={2} vAlign="center" wrap="wrap">
                  <Heading level={2}>{profile.displayName}</Heading>
                  <Badge label={auth.team.name} variant="neutral" />
                </HStack>
                {profile.displayName !== profile.email && (
                  <Text type="supporting" color="secondary" className="iorio-profile-email">
                    {profile.email}
                  </Text>
                )}
                {!profile.username && (
                  <Text type="supporting" color="secondary">
                    Agregá un username para que el equipo te reconozca más fácil.
                  </Text>
                )}
              </VStack>
            </HStack>

            <Collapsible
              trigger="Editar username y foto"
              isOpen={isEditing}
              onOpenChange={setIsEditing}
              className="iorio-profile-editor"
            >
              <form onSubmit={submit}>
                <VStack gap={5}>
                  <ValidationErrors errors={bannerErrors} />

                  <TextInput
                    label="Username"
                    htmlName="username"
                    value={form.data.username}
                    onChange={(value) => form.setData('username', value)}
                    placeholder="Tu nombre en el equipo"
                    status={usernameStatus}
                  />

                  <FileInput
                    label="Foto de perfil"
                    value={form.data.profilePhoto}
                    onChange={(file) =>
                      form.setData('profilePhoto', Array.isArray(file) ? file[0] : file)
                    }
                    accept="image/png,image/jpeg,image/webp"
                    maxSize={5 * 1024 * 1024}
                    isOptional
                    mode="dropzone"
                    description="PNG, JPG o WebP. Máximo 5 MB."
                    placeholder="Subir foto"
                  />

                  {form.progress && (
                    <Text type="supporting" color="secondary">
                      Subiendo foto: {form.progress.percentage ?? 0}%
                    </Text>
                  )}

                  <HStack gap={2} hAlign="end" wrap="wrap">
                    <Button label="Cancelar" variant="ghost" onClick={cancelEditing} />
                    <Button
                      label="Guardar perfil"
                      variant="primary"
                      type="submit"
                      isDisabled={form.processing}
                      isLoading={form.processing}
                    />
                  </HStack>
                </VStack>
              </form>
            </Collapsible>
          </VStack>
        </Card>

        <Card padding={6} className="iorio-users-card iorio-profile-card iorio-user-api-key-card">
          <VStack gap={5}>
            <VStack gap={2}>
              <Heading level={2}>API key</Heading>
              <Text type="supporting" color="secondary">
                Credencial personal para conectar tus herramientas con Iorio.
              </Text>
            </VStack>

            <TextInput label="Clave" value={apiKey.token} isDisabled />

            <MetadataList columns="multi">
              <MetadataListItem label="Autenticación">
                <Code>Authorization: Bearer {'{token}'}</Code>
              </MetadataListItem>
              <MetadataListItem label="Última actividad">
                {apiKey.lastUsedAt
                  ? new Date(apiKey.lastUsedAt).toLocaleString('es-AR')
                  : 'Sin actividad registrada'}
              </MetadataListItem>
            </MetadataList>

            <Form method="post" action={api_key_path()}>
              {({ processing }) => (
                <Button
                  label="Regenerar API key"
                  variant="secondary"
                  type="submit"
                  isDisabled={processing}
                  isLoading={processing}
                />
              )}
            </Form>
          </VStack>
        </Card>

        <Card padding={6} className="iorio-users-card iorio-profile-card">
          <VStack gap={5}>
            <VStack gap={2}>
              <Heading level={2}>Integración</Heading>
              <Text type="supporting" color="secondary">
                Consultá proyectos y gestioná tus partes desde cualquier herramienta.
              </Text>
            </VStack>

            <List density="balanced" hasDividers>
              <ListItem
                label={<Code>{api_projects_path()}</Code>}
                description="Catálogo de proyectos activos (id + nombre)."
                startContent={<Badge label="GET" variant="blue" />}
              />
              <ListItem
                label={<Code>{api_reports_path()}</Code>}
                description={
                  <Text type="supporting" color="secondary">
                    Listá tus partes; <Code>?from=</Code> y <Code>?to=</Code> son opcionales.
                  </Text>
                }
                startContent={<Badge label="GET" variant="blue" />}
              />
              <ListItem
                label={<Code>{api_report_path(':date')}</Code>}
                description="Leé el parte de un día (última versión)."
                startContent={<Badge label="GET" variant="blue" />}
              />
              <ListItem
                label={<Code>{api_report_path(':date')}</Code>}
                description={
                  <Text type="supporting" color="secondary">
                    Guardá una nueva versión. Mandá <Code>entries</Code> completo: lo que no envíes
                    se saca del parte.
                  </Text>
                }
                startContent={<Badge label="POST" variant="orange" />}
              />
            </List>
          </VStack>
        </Card>

        <HStack hAlign="end">
          <Button
            label="Cerrar sesión"
            variant="ghost"
            icon={<LogOut size={16} />}
            onClick={() => router.delete(destroy_session_path())}
          />
        </HStack>
      </section>
    </VStack>
  );
};

export default Show;
